import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  updateProfile,
  setPersistence
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot
} from 'firebase/firestore';
import { auth, googleProvider, db, browserSessionPersistence } from '../firebaseClient';

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  role: string;
  tenantId: string | null;
  [key: string]: any;
}

export interface UserProfile {
  userId: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  tenantId?: string;
  [key: string]: any;
}

interface UserContextType {
  user: User | null;
  firebaseUser: any | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  signUp: (email: string, password: string, additionalData?: any) => Promise<any>;
  signOutUser: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<boolean>;
  updateUserPassword: (newPassword: string) => Promise<void>;
  updateUserProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  createUserProfile: (userData: UserProfile) => Promise<void>;
  refreshUserClaims: () => Promise<boolean>;
  isGlobalAdmin: () => boolean;
  isTenantAdmin: () => boolean;
  isTenantUser: () => boolean;
  hasRole: (role: string) => boolean;
  belongsToTenant: (tenantId: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<any | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get custom claims
  const getCustomClaims = async (user: any) => {
    try {
      const tokenResult = await user.getIdTokenResult();
      return tokenResult.claims;
    } catch (error) {
      console.error('Error getting custom claims:', error);
      return {};
    }
  };

  // Helper function to get user profile from Firestore
  const getUserProfile = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? (userDoc.data() as UserProfile) : null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  // Authentication state observer
  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get custom claims (role, tenantId)
          const claims = await getCustomClaims(firebaseUser);

          // Get user profile from Firestore
          const profile = await getUserProfile(firebaseUser.uid);

          setFirebaseUser(firebaseUser);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            role: (claims.role as string) || 'tenantUser',
            tenantId: (claims.tenantId as string) || null,
            ...claims
          });

          setUserProfile(profile);

          // Set up real-time listener for user profile
          if (profile) {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            unsubscribeProfile = onSnapshot(userDocRef, (doc) => {
              if (doc.exists()) {
                setUserProfile(doc.data() as UserProfile);
              }
            });
          }
        } catch (error: any) {
          console.error('Error setting up user:', error);
          setError(error.message);
        }
      } else {
        setUser(null);
        setFirebaseUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  // Helper to format auth errors into user-friendly messages
  const formatAuthError = (error: any) => {
    const errorCode = error.code || '';
    const errorMessages: Record<string, string> = {
      'auth/invalid-email': 'Invalid email address format.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-credential': 'Invalid email or password. Please check your credentials.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password is too weak. Please use at least 8 characters with mixed case, numbers, and symbols.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
      'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
      'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups for this site.',
      'auth/popup-closed-by-user': 'Sign-in popup was closed before completion.',
      'auth/network-request-failed': 'Network error. Please check your internet connection.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later or reset your password.',
      'auth/requires-recent-login': 'This operation requires recent authentication. Please sign in again.'
    };

    return errorMessages[errorCode] || error.message || 'An unexpected error occurred. Please try again.';
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      // Set session persistence before signing in
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error: any) {
      const friendlyError = formatAuthError(error);
      setError(friendlyError);
      throw new Error(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      // Set session persistence before signing in
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error: any) {
      const friendlyError = formatAuthError(error);
      setError(friendlyError);
      throw new Error(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string, additionalData: any = {}) => {
    try {
      setError(null);
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name if provided
      if (additionalData.displayName) {
        await updateProfile(result.user, {
          displayName: additionalData.displayName
        });
      }

      // Send email verification (optional but recommended)
      if (additionalData.sendVerification !== false) {
        try {
          await sendEmailVerification(result.user);
        } catch (verifyError) {
          console.warn('Failed to send verification email:', verifyError);
          // Don't fail signup if verification email fails
        }
      }

      return result.user;
    } catch (error: any) {
      const friendlyError = formatAuthError(error);
      setError(friendlyError);
      throw new Error(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      setError(null);

      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
      setUserProfile(null);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  // Send verification email
  const sendVerificationEmail = async () => {
    try {
      setError(null);
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return true;
      }
      return false;
    } catch (error: any) {
      const friendlyError = formatAuthError(error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Update password
  const updateUserPassword = async (newPassword: string) => {
    try {
      setError(null);
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, newPassword);
      }
    } catch (error: any) {
      const friendlyError = formatAuthError(error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Update user profile
  const updateUserProfile = async (profileData: Partial<UserProfile>) => {
    try {
      setError(null);
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          ...profileData,
          updatedAt: new Date()
        });
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  // Create initial user profile
  const createUserProfile = async (userData: UserProfile) => {
    try {
      setError(null);
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          ...userData,
          userId: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  // Refresh user claims after backend updates
  const refreshUserClaims = async () => {
    try {
      if (auth.currentUser) {
        // Force token refresh to get updated custom claims
        await auth.currentUser.getIdToken(true);

        // Get updated claims
        const claims = await getCustomClaims(auth.currentUser);

        // Update user state with new claims
        setUser((prevUser) => {
          if (!prevUser) return null;
          return {
            ...prevUser,
            role: (claims.role as string) || 'tenantUser',
            tenantId: (claims.tenantId as string) || null,
            ...claims
          };
        });

        return true;
      }
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error refreshing user claims:', error);
      }
      return false;
    }
  };

  // Helper functions for role checking - memoized to prevent infinite re-renders
  const isGlobalAdmin = useCallback(() => user?.role === 'globalAdmin', [user?.role]);
  const isTenantAdmin = useCallback(() => user?.role === 'tenantAdmin', [user?.role]);
  const isTenantUser = useCallback(() => user?.role === 'tenantUser', [user?.role]);
  const hasRole = useCallback((role: string) => user?.role === role, [user?.role]);
  const belongsToTenant = useCallback((tenantId: string) => user?.tenantId === tenantId, [user?.tenantId]);

  const value: UserContextType = {
    user,
    firebaseUser,
    userProfile,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signUp,
    signOutUser,
    resetPassword,
    sendVerificationEmail,
    updateUserPassword,
    updateUserProfile,
    createUserProfile,
    refreshUserClaims,
    isGlobalAdmin,
    isTenantAdmin,
    isTenantUser,
    hasRole,
    belongsToTenant
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
